import {
  CUSTOM_COMPONENT_PREFIX,
  isCustomComponentLayer,
  type CompositionLayer,
  type VideoComposition,
} from "./types";
import {
  CURRENT_SCHEMA_VERSION,
  type CustomComponentSchema,
  type FieldSchema,
  type TextElementStyle,
  type TextElementValue,
} from "./custom-component-schema";

/**
 * Apply schema-declared defaults to every custom-component layer in a
 * composition. Layers whose schema is missing pass through unchanged —
 * the renderer still mounts the component with whatever props the
 * composition supplied (the component's own JSX defaults take over).
 *
 * Defaults are filled at render time so a sparse `composition.json`
 * still produces a correct render. The input composition is not mutated.
 */
export function applySchemaDefaultsToComposition(
  composition: VideoComposition,
  schemas: Record<string, CustomComponentSchema | undefined>,
): VideoComposition {
  let changed = false;
  const layers = composition.layers.map((layer) => {
    const next = applySchemaDefaultsToLayer(layer, schemas);
    if (next !== layer) changed = true;
    return next;
  });
  return changed ? { ...composition, layers } : composition;
}

function applySchemaDefaultsToLayer(
  layer: CompositionLayer,
  schemas: Record<string, CustomComponentSchema | undefined>,
): CompositionLayer {
  if (!isCustomComponentLayer(layer)) return layer;
  // `transpile-custom-components` rewrites composition refs from .tsx → .js
  // at save time, so the runtime layer ref ends in `.js`. We tolerate `.tsx`
  // here for the case where defaults are applied before transpile (tests,
  // dev-time previews).
  const filename = layer.component
    .slice(CUSTOM_COMPONENT_PREFIX.length)
    .replace(/\.tsx$/, ".js");
  const schema = schemas[filename];
  if (!schema) return layer;
  return { ...layer, props: applySchemaDefaults(layer.props, schema) };
}

/**
 * Merge a single component's prop object with its schema's defaults.
 *
 * Exported separately so the editor's properties form can seed missing
 * fields with defaults without rendering through a `<Player>`. Props
 * declared by the component but absent from the schema pass through
 * untouched.
 */
export function applySchemaDefaults(
  props: Record<string, unknown> | undefined,
  schema: CustomComponentSchema,
): Record<string, unknown> {
  // A schema whose version exceeds what this build knows how to
  // interpret may have restructured field types in ways we'd
  // mis-default. Pass the props through untouched and let the component's
  // own JSX defaults take over.
  if (schema.version > CURRENT_SCHEMA_VERSION) {
    console.warn(
      `[ad-studio-renderer] schema "${schema.name}" version ${schema.version} is newer than supported ${CURRENT_SCHEMA_VERSION} — skipping defaults`,
    );
    return { ...(props ?? {}) };
  }
  const out: Record<string, unknown> = { ...(props ?? {}) };
  for (const [name, field] of Object.entries(schema.fields)) {
    out[name] = applyFieldDefault(out[name], field);
  }
  return out;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function applyFieldDefault(value: unknown, field: FieldSchema): unknown {
  switch (field.type) {
    case "number":
    case "string":
    case "enum":
    case "boolean":
    case "color":
    case "asset":
      return value ?? field.default;
    case "text-element":
      return applyTextElementDefault(value, field.defaults);
    case "array": {
      // Composition.json could carry a non-array at this path from a
      // legacy schema or a hand-edit. Falling through to map() on a
      // non-array would crash the renderer; recover to the field's
      // default (or `[]`) and surface the bad value via a warning.
      if (value !== undefined && !Array.isArray(value)) {
        console.warn(
          `[ad-studio-renderer] array field expected an array (got ${typeof value}); falling back to default`,
        );
      }
      const items: unknown[] = Array.isArray(value)
        ? value
        : Array.isArray(field.default)
          ? field.default
          : [];
      return items.map((item) => applyFieldDefault(item, field.of));
    }
    case "object": {
      // `?? field.default ?? {}` only catches null/undefined — a string
      // value would slip through and `{ ...value }` would spread it into
      // character-index keys ({"0":"h","1":"e",...}), silently corrupting
      // the layer's props. Branch on plain-object-ness explicitly.
      if (value !== undefined && !isPlainObject(value)) {
        console.warn(
          `[ad-studio-renderer] object field expected an object (got ${typeof value}); falling back to default`,
        );
      }
      const base: Record<string, unknown> = isPlainObject(value)
        ? value
        : isPlainObject(field.default)
          ? field.default
          : {};
      const out: Record<string, unknown> = { ...base };
      for (const [k, sub] of Object.entries(field.fields)) {
        out[k] = applyFieldDefault(out[k], sub);
      }
      return out;
    }
    default: {
      // Unrecognized field type — pass the raw value through rather than
      // overwriting the prop with `undefined`. The version gate at the
      // top of `applySchemaDefaults` catches schemas authored against a
      // newer build; this catches typos or new field types that landed
      // in code without a `CURRENT_SCHEMA_VERSION` bump.
      const unknownType = (field as { type?: unknown }).type;
      console.warn(
        `[ad-studio-renderer] unrecognized field type "${String(unknownType)}" — preserving value as-is`,
      );
      return value;
    }
  }
}

function applyTextElementDefault(
  value: unknown,
  defaults: { text: string; style?: TextElementStyle },
): TextElementValue {
  // Sparse / legacy compositions sometimes store a bare string at this
  // path (e.g. a hand-edit that wrote `"Hello"` instead of `{ text:
  // "Hello" }`). Preserve the user's content rather than silently
  // overriding it with `defaults.text`.
  if (typeof value === "string") {
    return { text: value, style: { ...(defaults.style ?? {}) } };
  }
  // Style is a shallow merge: defaults supply the agent-designed baseline,
  // and any override in the composition wins per-key. Keys not listed in the
  // schema's `editableStyle` are still respected here so the component
  // receives the full style object regardless of which keys the editor
  // happened to surface.
  if (isPlainObject(value)) {
    const v = value as Partial<TextElementValue>;
    const styleOverride = isPlainObject(v.style) ? v.style : {};
    return {
      text: typeof v.text === "string" ? v.text : defaults.text,
      style: { ...(defaults.style ?? {}), ...styleOverride },
    };
  }
  if (value !== undefined) {
    console.warn(
      `[ad-studio-renderer] text-element value is not a string or object (got ${typeof value}); using defaults`,
    );
  }
  return { text: defaults.text, style: { ...(defaults.style ?? {}) } };
}
